import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { packageApi } from "../../api/packageApi";
import SpareLinkLogo from "../client/Logos/Spare_Link_SA_Logo.png";
import ModernNestLogo from "../client/Logos/modernnest.png";
import MakhutjaLeatherLogo from "../client/Logos/makhutja_leather.jpg";
import LebabaLogo from "../client/Logos/Lebaba.png";
import Bloom_Beauty_Sales from "../client/Logos/Bloom_Beauty_Sales.png";
import "./Ecommerce.css";

/* ============================================================
   Static content
   ============================================================ */

/*
  LIVE CLIENT STORES
  Client logos are imported from src/pages/client/Logos. If a logo fails
  to load, the card shows a clean monogram instead.
*/
const clients = [
  {
    name: "Modern Nest",
    url: "https://modernnest.co.za",
    domain: "modernnest.co.za",
    category: "Home & lifestyle",
    logo: ModernNestLogo,
  },
  {
    name: "SpareLink SA",
    url: "https://sparelink-sa.co.za",
    domain: "sparelink-sa.co.za",
    category: "Spare parts",
    logo: SpareLinkLogo,
  },
  {
    name: "Makhutsa Leather Collections",
    url: "https://makhutsa-leather-collections.co.za",
    domain: "makhutsa-leather-collections.co.za",
    category: "Leather goods",
    logo: MakhutjaLeatherLogo,
  },
  {
    name: "Lebaba Inware",
    url: "https://lebaba-inware.co.za",
    domain: "lebaba-inware.co.za",
    category: "Online store",
    logo: LebabaLogo,
  },
  {
    name: "Bloom Beauty Sales",
    url: "https://bloom-beauty-sales.co.za",
    domain: "bloom-beauty-sales.co.za",
    category: "Beauty products",
    logo: Bloom_Beauty_Sales,
  },
];

const heroPoints = [
  "Free trial available",
  "Setup done for you",
  "Local payments built in",
];

const sellingFeatures = [
  {
    icon: "fa-layer-group",
    title: "Sell more than products",
    description:
      "Physical and digital products, livestock and services, all from one storefront.",
  },
  {
    icon: "fa-search",
    title: "Easy to browse",
    description:
      "Search, filters, sorting and full product details help customers find what they want.",
  },
  {
    icon: "fa-shopping-cart",
    title: "Simple checkout",
    description:
      "Customers add to cart and check out with the delivery and payment options you offer.",
  },
  {
    icon: "fa-calendar-check",
    title: "Online bookings",
    description:
      "Let customers book your services online, with online or in-person options where offered.",
  },
  {
    icon: "fa-user-circle",
    title: "Customer accounts",
    description:
      "Customers view orders, track deliveries and manage their profile in one place.",
  },
  {
    icon: "fa-star",
    title: "Reviews and promotions",
    description:
      "Build trust with customer reviews and run promotions to bring shoppers back.",
  },
];

const teamFeatures = [
  { icon: "fa-boxes", text: "Products, categories and stock" },
  { icon: "fa-receipt", text: "Orders, customers and bookings" },
  { icon: "fa-truck", text: "Delivery methods" },
  { icon: "fa-percent", text: "Promotions, banners and pages" },
  { icon: "fa-palette", text: "Store theme and branding" },
  { icon: "fa-chart-bar", text: "Sales reports and reviews" },
  { icon: "fa-shopping-basket", text: "Abandoned cart follow-up" },
  { icon: "fa-headset", text: "Customer support tools" },
];

const paymentMethods = [
  {
    name: "PayFast",
    note: "Cards, instant EFT and more",
    icon: "fa-credit-card",
  },
  { name: "Ozow", note: "Pay directly from your bank", icon: "fa-university" },
  {
    name: "Payflex",
    note: "Buy now, pay later at participating stores",
    icon: "fa-calendar-alt",
    isNew: true,
  },
];

const steps = [
  {
    icon: "fa-comments",
    title: "Choose your package",
    text: "Pick a plan and tell us about your business and store.",
  },
  {
    icon: "fa-cogs",
    title: "We set up your store",
    text: "Your storefront, branding and essential functionality are configured for you.",
  },
  {
    icon: "fa-rocket",
    title: "Start selling",
    text: "Add your products, share your store link and start reaching customers.",
  },
];

const faqs = [
  {
    q: "Do I need technical skills?",
    a: "No. We handle the technical setup, including your storefront, branding and essential store functionality, so you can focus on your products and customers.",
  },
  {
    q: "Can I try Toora Flex before I commit?",
    a: "Yes. Every plan has a free trial option. Choose it when you request your store and we will take you through the next steps.",
  },
  {
    q: "What can I sell?",
    a: "Physical products, digital products, livestock and services. Services can be booked online, with online or in-person options where you offer them.",
  },
  {
    q: "Which payment methods are supported?",
    a: "PayFast, Ozow and Payflex. Payflex buy-now-pay-later is available at participating stores.",
  },
  {
    q: "What is included in the subscription?",
    a: "Each package includes the platform and hosting services listed on its card. Domain registration, professional email, payment gateway charges, advertising and additional services may be subject to package terms or separate charges.",
  },
  {
    q: "Can I upgrade later?",
    a: "Yes. Start with the plan that suits your business today and move to a larger plan as your store grows.",
  },
];

const previewCategories = [
  "All",
  "Products",
  "Digital",
  "Livestock",
  "Services",
];

const previewItems = [
  {
    icon: "fa-shopping-bag",
    name: "Leather satchel",
    type: "Product",
    price: "R 649",
  },
  {
    icon: "fa-file-download",
    name: "Garden planner",
    type: "Digital",
    price: "R 99",
  },
  {
    icon: "fa-paw",
    name: "Boer goat kid",
    type: "Livestock",
    price: "R 2 400",
  },
  {
    icon: "fa-calendar-check",
    name: "Hair styling",
    type: "Book · 45 min",
    price: "R 350",
  },
];

const dashboardBars = [38, 52, 44, 70, 58, 82, 66];
const dashboardDays = ["M", "T", "W", "T", "F", "S", "S"];

/* ============================================================
   Small components
   ============================================================ */

const getInitials = (name) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join("");

const ClientLogo = ({ client, tone }) => {
  const [failed, setFailed] = useState(false);

  if (client.logo && !failed) {
    return (
      <img
        src={client.logo}
        alt={`${client.name} logo`}
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className="ecom-monogram" data-tone={tone} aria-hidden="true">
      {getInitials(client.name)}
    </span>
  );
};

/* ============================================================
   Page
   ============================================================ */

const Ecommerce = () => {
  const { user } = useAuth();
  const ecommerceStartTo =
    user?.Role === "admin"
      ? "/admin/packages"
      : user
        ? "/request-store"
        : "/register";
  const ecommerceStartState = user
    ? undefined
    : { onboardingType: "ecommerce" };
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedPackages, setExpandedPackages] = useState(() => new Set());
  const [canScrollPackagesBack, setCanScrollPackagesBack] = useState(false);
  const [canScrollPackagesForward, setCanScrollPackagesForward] = useState(false);
  const [packagePage, setPackagePage] = useState(0);
  const [packagePageCount, setPackagePageCount] = useState(1);
  const packagesViewportRef = useRef(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  useEffect(() => {
    updatePackageCarouselControls();
    window.addEventListener("resize", updatePackageCarouselControls);
    return () =>
      window.removeEventListener("resize", updatePackageCarouselControls);
  }, [packages.length]);

  const getPackageStep = () => {
    const viewport = packagesViewportRef.current;
    const track = viewport?.firstElementChild;
    const firstCard = viewport?.querySelector(".ecom-plan");
    if (!viewport || !track || !firstCard) return viewport?.clientWidth || 0;

    const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
    return firstCard.offsetWidth + gap;
  };

  const updatePackageCarouselControls = () => {
    const viewport = packagesViewportRef.current;
    if (!viewport) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    const currentScroll = Math.min(viewport.scrollLeft, maxScroll);
    setCanScrollPackagesBack(currentScroll > 1);
    setCanScrollPackagesForward(currentScroll < maxScroll - 1);

    const track = viewport.firstElementChild;
    const firstCard = viewport.querySelector(".ecom-plan");
    if (!track || !firstCard) return;

    const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
    const step = firstCard.offsetWidth + gap;
    if (step <= 0) return;

    const visiblePackages = Math.max(
      1,
      Math.round((viewport.clientWidth + gap) / step),
    );
    const pageCount = Math.max(1, packages.length - visiblePackages + 1);
    setPackagePageCount(pageCount);
    setPackagePage(
      Math.min(pageCount - 1, Math.round(currentScroll / step)),
    );
  };

  const scrollPackages = (direction) => {
    const viewport = packagesViewportRef.current;
    if (!viewport) return;

    viewport.scrollBy({
      left: direction * getPackageStep(),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };

  const goToPackage = (index) => {
    const viewport = packagesViewportRef.current;
    if (!viewport) return;

    const maxScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    viewport.scrollTo({
      left: Math.min(maxScroll, Math.max(0, index * getPackageStep())),
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth",
    });
  };

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await packageApi.getPublicPackages(true);

      let packagesData = [];
      if (response.success) {
        packagesData = response.data || [];
      } else if (Array.isArray(response)) {
        packagesData = response;
      } else if (response.packages) {
        packagesData = response.packages;
      }

      packagesData = packagesData.filter((pkg) => pkg.IsActive !== false);
      packagesData.sort((a, b) => (a.Price || 0) - (b.Price || 0));

      setPackages(packagesData);
    } catch (err) {
      console.error("Error fetching packages:", err);
      setError("Failed to load packages. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price || 0);

  const getPackageFeatures = (pkg) => {
    const features = [];

    features.push(
      pkg.ProductLimit
        ? `Up to ${pkg.ProductLimit} products`
        : "Unlimited products",
    );

    let featuresObj = pkg.Features;
    if (typeof pkg.Features === "string") {
      try {
        featuresObj = JSON.parse(pkg.Features);
      } catch (e) {
        featuresObj = {};
      }
    }

    if (featuresObj && typeof featuresObj === "object") {
      Object.values(featuresObj).forEach((value) => {
        if (value && typeof value === "string" && value.trim() !== "") {
          features.push(value);
        }
      });
    }

    return features;
  };

  const isFeatured = (pkg, index) => {
    const total = packages.length;
    if (total === 0) return false;
    if (String(pkg.Tier).toLowerCase() === "growth") return true;
    if (total === 3 && index === 1) return true;
    if (total > 1 && index === Math.floor(total / 2)) return true;
    return false;
  };

  const getPlanTagline = (tier) => {
    switch (tier) {
      case "Lite":
        return "A simple, affordable store for small businesses.";
      case "Starter":
        return "Everything you need to launch and grow your store.";
      case "Growth":
        return "More capacity and tools for businesses ready to scale.";
      case "Pro":
        return "Advanced features for high-growth businesses.";
      default:
        return "A professional online store solution.";
    }
  };

  const toggleExpand = (packageId) => {
    setExpandedPackages((previous) => {
      const next = new Set(previous);
      if (next.has(packageId)) {
        next.delete(packageId);
      } else {
        next.add(packageId);
      }
      return next;
    });
  };

  const MAX_VISIBLE_FEATURES = 4;

  const getDisplayFeatures = (features, expanded) => {
    const hasMore = features.length > MAX_VISIBLE_FEATURES;

    return {
      visible:
        hasMore && !expanded
          ? features.slice(0, MAX_VISIBLE_FEATURES)
          : features,
      hasMore,
      expanded,
      hiddenCount: features.length - MAX_VISIBLE_FEATURES,
    };
  };

  return (
    <div className="ecom">
      {/* ================= HERO ================= */}
      <section className="ecom-hero">
        <div className="ecom-container ecom-hero-grid">
          <div className="ecom-hero-copy">
            <a href="#pricing" className="ecom-pill">
              <span className="ecom-pill-tag">New</span>
              Payflex buy-now-pay-later now supported
              <i className="fas fa-chevron-right" aria-hidden="true"></i>
            </a>

            <h1>Launch your online store without the technical work.</h1>

            <p className="ecom-hero-lead">
              Toora Flex gives South African businesses a complete storefront
              for products, livestock, services and bookings, with local
              payments built in. We handle the setup. You start selling.
            </p>

            <div className="ecom-hero-actions">
              <Link
                to={ecommerceStartTo}
                state={ecommerceStartState}
                className="ecom-btn ecom-btn-primary"
              >
                Start your store
              </Link>
              <a href="#pricing" className="ecom-btn ecom-btn-secondary">
                View pricing
              </a>
            </div>

            <ul className="ecom-hero-points">
              {heroPoints.map((point) => (
                <li key={point}>
                  <i className="fas fa-check" aria-hidden="true"></i>
                  {point}
                </li>
              ))}
            </ul>
          </div>

          {/* Storefront preview */}
          <div className="ecom-hero-visual" aria-hidden="true">
            <div className="ecom-window">
              <div className="ecom-window-bar">
                <div className="ecom-window-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <div className="ecom-window-address">
                  <i className="fas fa-lock"></i>
                  yourstore.co.za
                </div>
              </div>

              <div className="ecom-store">
                <div className="ecom-store-header">
                  <strong>Your Store</strong>
                  <div className="ecom-store-search">
                    <i className="fas fa-search"></i>
                    <span>Search products and services</span>
                  </div>
                  <div className="ecom-store-cart">
                    <i className="fas fa-shopping-cart"></i>
                    <b>2</b>
                  </div>
                </div>

                <div className="ecom-store-chips">
                  {previewCategories.map((cat, i) => (
                    <span key={cat} className={i === 0 ? "is-active" : ""}>
                      {cat}
                    </span>
                  ))}
                </div>

                <div className="ecom-store-grid">
                  {previewItems.map((item) => (
                    <div className="ecom-store-card" key={item.name}>
                      <div className="ecom-store-thumb">
                        <i className={`fas ${item.icon}`}></i>
                      </div>
                      <h4>{item.name}</h4>
                      <small>{item.type}</small>
                      <strong>{item.price}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= CLIENTS ================= */}
      <section className="ecom-section ecom-clients" id="clients">
        <div className="ecom-container">
          <div className="ecom-heading ecom-heading-center">
            <p className="ecom-eyebrow">Live stores</p>
            <h2>Businesses already selling on Toora Flex</h2>
            <p>
              Visit the stores below to see what a Toora Flex storefront looks
              like for real South African businesses.
            </p>
          </div>

          <div
            className="ecom-client-carousel"
            role="region"
            aria-label="Live client stores"
          >
            <div className="ecom-client-track">
              {[false, true].map((isClone) => (
                <ul
                  className={`ecom-client-grid${isClone ? " is-clone" : ""}`}
                  key={isClone ? "clone" : "clients"}
                  aria-label={isClone ? undefined : "Client stores"}
                  aria-hidden={isClone ? "true" : undefined}
                >
                  {clients.map((client, index) => (
                    <li key={client.url}>
                      <a
                        className="ecom-client-card"
                        href={client.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Visit ${client.name} (opens in a new tab)`}
                        tabIndex={isClone ? -1 : undefined}
                      >
                        <div className="ecom-client-logo">
                          <ClientLogo client={client} tone={index % 4} />
                        </div>
                        <div className="ecom-client-body">
                          <h3>{client.name}</h3>
                          {client.category && <span>{client.category}</span>}
                        </div>
                        <div className="ecom-client-link">
                          <span>{client.domain}</span>
                          <i
                            className="fas fa-arrow-up-right-from-square"
                            aria-hidden="true"
                          ></i>
                        </div>
                      </a>
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="ecom-section ecom-section-alt">
        <div className="ecom-container">
          <div className="ecom-heading">
            <p className="ecom-eyebrow">The storefront</p>
            <h2>Everything your customers need to buy with confidence</h2>
            <p>
              One storefront for products, services and bookings. Products and
              delivery options depend on how each store is configured.
            </p>
          </div>

          <div className="ecom-feature-grid">
            {sellingFeatures.map((feature) => (
              <div className="ecom-feature" key={feature.title}>
                <div className="ecom-icon">
                  <i className={`fas ${feature.icon}`} aria-hidden="true"></i>
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= DASHBOARD ================= */}
      <section className="ecom-section">
        <div className="ecom-container ecom-split">
          <div className="ecom-split-copy">
            <p className="ecom-eyebrow">The dashboard</p>
            <h2>One place to run the whole store</h2>
            <p>
              Your team gets the tools to keep products, orders and customers
              organised, without any technical work.
            </p>

            <ul className="ecom-checklist">
              {teamFeatures.map((item) => (
                <li key={item.text}>
                  <span>
                    <i className={`fas ${item.icon}`} aria-hidden="true"></i>
                  </span>
                  {item.text}
                </li>
              ))}
            </ul>
          </div>

          <div className="ecom-dash" aria-hidden="true">
            <div className="ecom-dash-top">
              <strong>Sales this week</strong>
              <span>Sample view</span>
            </div>

            <div className="ecom-dash-stats">
              <div>
                <small>Orders</small>
                <b>126</b>
              </div>
              <div>
                <small>Bookings</small>
                <b>34</b>
              </div>
              <div>
                <small>Abandoned carts</small>
                <b>9</b>
              </div>
            </div>

            <div className="ecom-dash-chart">
              {dashboardBars.map((height, i) => (
                <div className="ecom-dash-bar" key={i}>
                  <i style={{ height: `${height}%` }}></i>
                  <span>{dashboardDays[i]}</span>
                </div>
              ))}
            </div>

            <div className="ecom-dash-orders">
              <div>
                <span>#1042</span>
                <em className="is-green">Shipped</em>
              </div>
              <div>
                <span>#1041</span>
                <em>Processing</em>
              </div>
              <div>
                <span>#1040</span>
                <em className="is-green">Delivered</em>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= PAYMENTS ================= */}
      <section className="ecom-section ecom-section-alt ecom-payments">
        <div className="ecom-container">
          <div className="ecom-heading ecom-heading-center">
            <p className="ecom-eyebrow">Payments</p>
            <h2>Payment methods your customers already know</h2>
            <p>
              Connect PayFast, Ozow and the newly added Payflex integration.
            </p>
          </div>

          <div className="ecom-pay-grid">
            {paymentMethods.map((method) => (
              <div className="ecom-pay" key={method.name}>
                {method.isNew && <span className="ecom-pay-new">New</span>}
                <div className="ecom-icon">
                  <i className={`fas ${method.icon}`} aria-hidden="true"></i>
                </div>
                <strong>{method.name}</strong>
                <span>{method.note}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="pricing" className="ecom-section ecom-pricing">
        <div className="ecom-container">
          <div className="ecom-heading ecom-heading-center">
            <p className="ecom-eyebrow">Pricing</p>
            <h2>Choose the right store package</h2>
            <p>
              Start with the plan that suits your business today and upgrade as
              your store grows. Every plan has a free trial option.
            </p>
          </div>

          {loading ? (
            <div className="ecom-state" role="status">
              <div className="ecom-spinner" aria-hidden="true"></div>
              <p>Loading packages…</p>
            </div>
          ) : error ? (
            <div className="ecom-state ecom-state-error" role="alert">
              <i className="fas fa-exclamation-circle" aria-hidden="true"></i>
              <p>{error}</p>
              <button
                type="button"
                className="ecom-btn ecom-btn-secondary"
                onClick={fetchPackages}
              >
                Try again
              </button>
            </div>
          ) : packages.length === 0 ? (
            <div className="ecom-state">
              <i className="fas fa-box-open" aria-hidden="true"></i>
              <h3>No packages available</h3>
              <p>Please check back later for our available packages.</p>
            </div>
          ) : (
            <div className="ecom-plans-carousel">
              <div className="ecom-plans-stage">
                <div
                  className="ecom-plans-viewport"
                  ref={packagesViewportRef}
                  onScroll={updatePackageCarouselControls}
                  role="region"
                  aria-label="Ecommerce packages"
                  tabIndex={0}
                >
                  <div className="ecom-plans">
                    {packages.map((pkg, index) => {
                      const featured = isFeatured(pkg, index);
                      const features = getPackageFeatures(pkg);
                      const packageId = String(pkg.PackageID || `pkg-${index}`);
                      const { visible, hasMore, expanded, hiddenCount } =
                        getDisplayFeatures(
                          features,
                          expandedPackages.has(packageId),
                        );
                      const featureListId = `package-features-${packageId}`;

                      return (
                        <article
                          className={`ecom-plan ${featured ? "is-featured" : ""}`}
                          key={packageId}
                        >
                          {featured && (
                            <div className="ecom-plan-badge">Most popular</div>
                          )}

                          <h3>{pkg.Name || "Package"}</h3>
                          <p className="ecom-plan-tagline">
                            {getPlanTagline(pkg.Tier)}
                          </p>

                          <div className="ecom-plan-price">
                            <strong>{formatPrice(pkg.Price)}</strong>
                            <span>/{pkg.BillingPeriod || "month"}</span>
                          </div>

                          {pkg.SetupFee > 0 && (
                            <p className="ecom-plan-setup">
                              + {formatPrice(pkg.SetupFee)} once-off setup fee
                            </p>
                          )}

                          <ul id={featureListId} className="ecom-plan-features">
                            {visible.map((feature, featureIndex) => (
                              <li key={featureIndex}>
                                <i
                                  className="fas fa-check"
                                  aria-hidden="true"
                                ></i>
                                <span>{feature}</span>
                              </li>
                            ))}
                          </ul>

                          {hasMore && (
                            <button
                              type="button"
                              className="ecom-plan-more"
                              onClick={() => toggleExpand(packageId)}
                              aria-expanded={expanded}
                              aria-controls={featureListId}
                            >
                              {expanded
                                ? "Show less"
                                : `Show ${hiddenCount} more`}
                              <i
                                className={`fas ${expanded ? "fa-chevron-up" : "fa-chevron-down"}`}
                                aria-hidden="true"
                              ></i>
                            </button>
                          )}

                          <div className="ecom-plan-actions">
                            <Link
                              to={
                                user?.Role === "admin"
                                  ? "/admin/packages"
                                  : user
                                    ? "/request-store"
                                    : "/register"
                              }
                              state={
                                user
                                  ? {
                                      selectedPackage: pkg.PackageID,
                                      trial: true,
                                    }
                                  : {
                                      onboardingType: "ecommerce",
                                      selectedPackage: pkg.PackageID,
                                      trial: true,
                                    }
                              }
                              className={`ecom-btn ${featured ? "ecom-btn-primary" : "ecom-btn-tonal"} ecom-btn-block`}
                            >
                              Start free trial
                            </Link>
                            <Link
                              to={
                                user?.Role === "admin"
                                  ? "/admin/packages"
                                  : user
                                    ? "/request-store"
                                    : "/register"
                              }
                              state={
                                user
                                  ? { selectedPackage: pkg.PackageID }
                                  : {
                                      onboardingType: "ecommerce",
                                      selectedPackage: pkg.PackageID,
                                    }
                              }
                              className="ecom-plan-link"
                            >
                              Or get started now
                            </Link>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </div>

                <div className="ecom-plans-controls">
                  <button
                    type="button"
                    className="ecom-plans-button"
                    onClick={() => scrollPackages(-1)}
                    disabled={!canScrollPackagesBack}
                    aria-label="Show previous ecommerce package"
                  >
                    <i className="fas fa-chevron-left" aria-hidden="true"></i>
                  </button>
                  <button
                    type="button"
                    className="ecom-plans-button"
                    onClick={() => scrollPackages(1)}
                    disabled={!canScrollPackagesForward}
                    aria-label="Show next ecommerce package"
                  >
                    <i className="fas fa-chevron-right" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              <div
                className="ecom-plans-dots"
                aria-label="Package carousel position"
              >
                {Array.from({ length: packagePageCount }, (_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`ecom-plans-dot ${packagePage === index ? "active" : ""}`}
                    onClick={() => goToPackage(index)}
                    aria-label={`Go to package slide ${index + 1}`}
                    aria-current={packagePage === index ? "true" : undefined}
                  />
                ))}
              </div>
            </div>
          )}

          <p className="ecom-note">
            <i className="fas fa-info-circle" aria-hidden="true"></i>
            Store subscriptions include the platform and hosting services
            specified in each package. Domain registration, professional email,
            payment gateway charges, advertising and additional services may be
            subject to package terms or separate charges.
          </p>
        </div>
      </section>

      {/* ================= HOW IT WORKS ================= */}
      <section className="ecom-section ecom-section-alt">
        <div className="ecom-container">
          <div className="ecom-heading ecom-heading-center">
            <p className="ecom-eyebrow">Get started</p>
            <h2>Launch your store in three steps</h2>
            <p>
              We handle the technical setup so you can focus on your products
              and customers.
            </p>
          </div>

          <ol className="ecom-steps">
            {steps.map((step, i) => (
              <li key={step.title}>
                <div className="ecom-step-top">
                  <div className="ecom-icon">
                    <i className={`fas ${step.icon}`} aria-hidden="true"></i>
                  </div>
                  <span className="ecom-step-num">0{i + 1}</span>
                </div>
                <h3>{step.title}</h3>
                <p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="ecom-section">
        <div className="ecom-container ecom-faq">
          <div className="ecom-heading">
            <p className="ecom-eyebrow">FAQ</p>
            <h2>Questions before you start?</h2>
            <p>
              Can’t find your answer? Request your store and we will walk you
              through it.
            </p>
          </div>

          <div className="ecom-faq-list">
            {faqs.map((item) => (
              <details className="ecom-faq-item" key={item.q}>
                <summary>
                  {item.q}
                  <i className="fas fa-chevron-down" aria-hidden="true"></i>
                </summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="ecom-cta">
        <div className="ecom-container">
          <div className="ecom-cta-box">
            <h2>Ready to sell online?</h2>
            <p>
              Join the businesses already selling on Toora Flex. Start with a
              free trial and we will help you get set up.
            </p>
            <div className="ecom-cta-actions">
              <Link
                to={ecommerceStartTo}
                state={ecommerceStartState}
                className="ecom-btn ecom-btn-light"
              >
                Start your store
              </Link>
              <a href="#pricing" className="ecom-btn ecom-btn-ghost">
                Compare plans
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ecommerce;
